import { RootState } from '../../app/store';
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import UserAPI from '../../API/userAPI';

const setCookie = (name: string, value: string, days = 30) => {
  const expires = new Date(Date.now() + days * 864e5).toUTCString();
  document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/; Secure; SameSite=None`;
};

const getCookie = (name: string) => {
  const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
  return match ? decodeURIComponent(match[2]) : null;
};

const deleteCookie = (name: string) => {
  document.cookie = `${name}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; Secure; SameSite=None`;
};

export const getSession = createAsyncThunk(
  'user/getSession',
  async () => {
    let token: string | null = null;

    try {
      const localToken = localStorage.getItem('usertoken');
      const setLoginInfo = localStorage.getItem('userAuthInfo');
      const logInfo = setLoginInfo ? JSON.parse(setLoginInfo) : null;

      token = localToken || (logInfo?.token ?? getCookie("authToken"));
    } catch (e) {
      console.error("Error while getting token:", e);
    }

    if (token) {
      try {
        const response = await UserAPI.getUserSession(token);
        if (!response || Object.keys(response).length === 0) {
          localStorage.removeItem('usertoken');
          localStorage.removeItem('userAuthInfo');
          localStorage.removeItem('profileImage');
          deleteCookie("authToken");
          return {};
        }
        setCookie("authToken", token);
        return response;
      } catch (err) {
        console.error("Session API failed", err);
        deleteCookie("authToken");
        return {};
      }
    }

    return {};
  }
);

export const logoutUser = createAsyncThunk(
  'user/logoutUser',
  async (token: string, { dispatch }) => {
    try {
      localStorage.removeItem('usertoken');
      localStorage.removeItem('userAuthInfo');
      localStorage.removeItem('profileImage');
      deleteCookie("authToken");
    } catch (e) {
      console.error("Error while clearing session:", e);
    }
    await UserAPI.logoutUser(token);

    dispatch(resetUser());
  }
);

export interface UserState {
  token: string;
  email: string;
  nicename: string;
  display_name: string;
  sessionLoaded: boolean;
  sessionLoading: boolean;
  sessionLoadError: boolean;
  loggingOut: boolean;
  access_token?: string;
  loadingAuth: boolean;
}

const initialState: UserState = {
  token: '',
  email: '',
  nicename: '',
  display_name: '',
  sessionLoaded: false,
  sessionLoading: false,
  sessionLoadError: false,
  loggingOut: false,
  access_token: '',
  loadingAuth: false,
}

export const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    setAuthInfo: (state, action) => {
      return {
        ...state, ...action.payload
      }
    },
    setEmail: (state, action) => {
      return {
        ...state,
        email: action.payload
      }
    },
    setLoadingAuth: (state, action) => {
      return {
        ...state,
        loadingAuth: action.payload
      }
    },
    resetUser: () => initialState,
  },
  extraReducers: builder => {
    builder.addCase(logoutUser.pending, (state, action) => {
      return {
        ...initialState,
        loggingOut: true
      }
    });
    builder.addCase(logoutUser.fulfilled, (state, action) => {
      return {
        ...initialState,
        loggingOut: false
      }
    });
    builder.addCase(logoutUser.rejected, (state, action) => {
      return {
        ...initialState,
        loggingOut: false
      }
    });
    builder.addCase(getSession.pending, (state, action) => {
      state.sessionLoading = true;
    });
    builder.addCase(getSession.fulfilled, (state, action) => {
      return {
        ...state, ...action.payload,
        sessionLoaded: true,
        sessionLoading: false
      }
    });
    builder.addCase(getSession.rejected, (state, action) => {
      state.sessionLoading = false;
      state.sessionLoadError = true;
    });
  }
});

export const { setAuthInfo, setEmail, setLoadingAuth, resetUser } = userSlice.actions;

export const selectLoggingOut = (state: RootState) => state.user.loggingOut;
export const selectUser = (state: RootState) => state.user;
export const selectSessionIsLoaded = (state: RootState) => state.user.sessionLoaded;
export const selectSessionIsLoading = (state: RootState) => state.user.sessionLoading;
export const selectSessionLoadError = (state: RootState) => state.user.sessionLoadError;
export const selectToken = (state: RootState) => state.user.token;
export const selectLoadingAuth = (state: RootState) => state.user.loadingAuth;

export default userSlice.reducer;